import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, output, signal, ViewChild } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { SdDialog } from '@solid-design-system/components';
import { Dialog } from '@util/dialog';
import { LoadingIndicatorDirective } from '@directives/loading-indicator.directive';
import { isClipboardAvailable } from '@util/clipboard';

import { DEFAULT_IMAGE_EXPORT_FORMAT, ImageExportFormat } from 'src/app/common/util/image-export';

export interface ImageExportDialogResult {
  format: ImageExportFormat;
  /** File name without extension, as edited by the user. */
  fileName: string;
}

/**
 * Preview dialog for the dashboard export as PNG (PDF opens the browser's print dialog directly). It shows the captured dashboard, the
 * active filters as pills and lets the user rename the file. Like `ExportDialogComponent` it never
 * exports itself: it emits {@link downloaded} / {@link copied}, the caller (DashboardComponent) does the
 * work and calls {@link hide} on success or {@link showError} on failure. Closing via "X" just ends the export.
 */
@Component({
  selector: 'app-image-export-dialog',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [TranslocoModule, LoadingIndicatorDirective],
  templateUrl: './image-export-dialog.component.html',
  styleUrl: './image-export-dialog.component.scss',
})
export class ImageExportDialogComponent extends Dialog {
  public readonly downloaded = output<ImageExportDialogResult>();
  public readonly copied = output<void>();

  protected readonly format = signal<ImageExportFormat>(DEFAULT_IMAGE_EXPORT_FORMAT);
  protected readonly fileName = signal<string>('');
  protected readonly previewUrl = signal<string | null>(null);
  protected readonly filterLabels = signal<string[]>([]);
  protected readonly busy = signal<boolean>(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly clipboardAvailable = isClipboardAvailable();

  @ViewChild('dialog')
  private readonly dialog!: ElementRef<SdDialog>;

  @ViewChild('nameInput')
  private readonly nameInput!: ElementRef<HTMLInputElement & { value: string }>;

  get canDownload(): boolean {
    return !this.busy() && this.previewUrl() !== null && this.readFileName().length > 0;
  }

  open(format: ImageExportFormat, fileName: string, previewUrl: string, filterLabels: string[] = []): void {
    this.format.set(format);
    this.fileName.set(fileName);
    this.previewUrl.set(previewUrl);
    this.filterLabels.set(filterLabels);
    this.busy.set(false);
    this.errorMessage.set(null);
    this.dialog?.nativeElement?.show();
  }

  hide(): void {
    this.busy.set(false);
    this.dialog?.nativeElement?.hide();
  }

  showError(message: string): boolean {
    this.busy.set(false);
    if (!this.isOpen()) {
      return false;
    }
    this.errorMessage.set(message);
    return true;
  }

  isOpen(): boolean {
    return this.dialog?.nativeElement?.open ?? false;
  }

  download(): void {
    if (!this.canDownload) {
      return;
    }
    this.errorMessage.set(null);
    this.busy.set(true);
    this.downloaded.emit({ format: this.format(), fileName: this.readFileName() });
  }

  copy(): void {
    if (this.busy()) {
      return;
    }
    this.errorMessage.set(null);
    this.copied.emit();
  }

  protected onAfterHide(): void {
    this.busy.set(false);
    this.errorMessage.set(null);
    this.previewUrl.set(null);
  }

  private readFileName(): string {
    return (this.nameInput?.nativeElement?.value ?? this.fileName()).trim();
  }
}
